/**
 * Verifica se l'utente ha almeno una delle capabilities richieste
 */
export function hasAnyCapability(
  userCapabilities: string[],
  requiredCapabilities: string[],
): boolean {
  // Se non ci sono capabilities richieste, la sezione è sempre accessibile
  if (requiredCapabilities.length === 0) return true
  
  // Wildcard = accesso completo
  if (userCapabilities.includes('*')) return true
  
  // Controlla se ha almeno una delle capabilities richieste
  return requiredCapabilities.some((cap) => userCapabilities.includes(cap))
}

/**
 * Verifica se una sezione è accessibile dall'utente
 */
export function canAccessSection(
  section: string,
  userCapabilities: string[],
  getSectionRequirements?: (sectionKey: string) => string[],
): boolean {
  // Wildcard = accesso completo
  if (userCapabilities.includes('*')) return true
  
  // Se non c'è getSectionRequirements, la sezione è accessibile solo con wildcard
  if (!getSectionRequirements) return false
  
  const required = getSectionRequirements(section)
  return hasAnyCapability(userCapabilities, required)
}

/**
 * Filtra le sezioni accessibili dall'utente
 */
export function filterAccessibleSections<T extends { key: string }>(
  items: T[],
  userCapabilities: string[],
  getSectionRequirements?: (sectionKey: string) => string[],
): T[] {
  return items.filter((item) => 
    canAccessSection(item.key, userCapabilities, getSectionRequirements)
  )
}

/**
 * Ottiene tutte le capabilities dell'utente (da ruoli + custom)
 */
export function getUserCapabilities(
  userRoles: string[],
  roleDefinitions: Array<{ name: string; capabilities?: string[] }>,
  customCapabilities: string[] = [],
  accountCapabilities: string[] = [],
): string[] {
  if (accountCapabilities.length > 0) {
    const allAccountCaps = [...accountCapabilities, ...customCapabilities]
    if (allAccountCaps.includes('*')) return ['*']
    return [...new Set(allAccountCaps)]
  }

  const capsFromRoles = userRoles.flatMap((roleName) => {
    const role = roleDefinitions.find((r) => r.name === roleName)
    return role?.capabilities || []
  })
  
  const allCaps = [...capsFromRoles, ...customCapabilities]
  
  // Se c'è il wildcard, basta quello
  if (allCaps.includes('*')) return ['*']
  
  // Rimuovi duplicati
  return [...new Set(allCaps)]
}
